import { type z } from "zod";

import { type PageId } from "@/constants";

import { type LoggerParsedPage } from "../../context";
import {
  type AmplitudeEventContainer,
  type AmplitudeEventId,
} from "../constants";

import { CUSTOMER_LTV_PAGE_EVENTS } from "./customer-ltv-page";
import { ORDERS_PAGE_EVENTS } from "./orders-page";
import { OVERVIEW_PAGE_EVENTS } from "./overview-page";
import { SALES_PAGE_EVENTS } from "./sales-page";
import { SAVED_VIEW_EVENTS } from "./saved-views";

export const AMPLITUDE_EVENTS = [
  ...SAVED_VIEW_EVENTS,
  ...OVERVIEW_PAGE_EVENTS,
  ...SALES_PAGE_EVENTS,
  ...ORDERS_PAGE_EVENTS,
  ...CUSTOMER_LTV_PAGE_EVENTS,
] as const;

export type AmplitudeEventPath<I extends PageId = PageId> = {
  readonly page: LoggerParsedPage<I>;
  readonly container: AmplitudeEventContainer;
};

export type AmplitudeEvent<T extends AmplitudeEventId | undefined = undefined> =
  T extends undefined
    ? (typeof AMPLITUDE_EVENTS)[number]
    : Extract<AmplitudeEvent, { id: T }>;

export type AmplitudeEventLabel<
  T extends AmplitudeEventId | undefined = undefined,
> = AmplitudeEvent<T>["label"];

export const getAmplitudeEvent = <T extends AmplitudeEventId>(
  id: T,
): AmplitudeEvent<T> => {
  const evt = [...AMPLITUDE_EVENTS].find((e) => e.id === id);
  if (!evt) {
    throw new Error(`The provided amplitude event ID '${id}' is invalid!`);
  }
  return evt as AmplitudeEvent<T>;
};

/**
 * Represents the context that pertains to a specific {@link AmplitudeEventId}.  This context will
 * be included under the 'eventDetails' attribute on the overall context provided to the
 * {@link Logger}'s {@link Logger.event} method.
 */
export type AmplitudeEventDetails<T extends AmplitudeEventId> =
  T extends AmplitudeEventId
    ? AmplitudeEvent<T> extends {
        /* eslint-disable-next-line @typescript-eslint/no-explicit-any */
        schema: infer S extends z.ZodObject<any, any, any, any, any>;
      }
      ? z.output<S>
      : // If the AmplitudeEvent is not associated with a schema, it means that it is not associated
        // with any additional event context.
        Record<never, never>
    : never;
